/** Small cron helpers shared by the schedule create form (build) and the
 *  schedule list (humanize). We only support the handful of presets the form
 *  exposes plus a best-effort humanizer for arbitrary 5-field expressions. */

export const WEEKDAYS = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
] as const;

const DAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/** Format minutes-past-midnight (local clock fields) as "9:00 AM". The cron
 *  fields are UTC on the backend, but for display we render the literal h/m. */
function fmtTime(hour: number, minute: number): string {
  const ampm = hour < 12 ? "AM" : "PM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, "0")} ${ampm}`;
}

export type RecurrencePreset = "daily" | "weekly" | "hourly";

/**
 * Shift a (hour, minute, weekday-set) from one zone to the other.
 *
 * Cron fields are stored/interpreted in UTC by the backend, but the user picks
 * and reads times in their LOCAL zone — so we convert at the edges. The minute
 * can roll the hour, and the hour can roll the day (e.g. 02:00 UTC Mon is the
 * previous evening locally), which shifts every weekday in the set. We build a
 * reference Date and read the other zone's fields off it so DST/offset is the
 * browser's, not hardcoded.
 *
 * `direction` "toUtc" treats the inputs as local (form → storage); "toLocal"
 * treats them as UTC (storage → display).
 */
function shiftCronTime(
  direction: "toUtc" | "toLocal",
  hour: number,
  minute: number,
  days: number[] | null,
): { hour: number; minute: number; days: number[] | null } {
  // Reference: a Sunday so weekday math is simple. Day index here is just a
  // carrier for the ±1 day rollover; we map it back onto the real day-set.
  const REF_DOW = 0; // Sunday
  let local: Date;
  if (direction === "toUtc") {
    // Inputs are local wall-clock → construct a local Date, read UTC fields.
    local = new Date(2024, 0, 7, hour, minute, 0, 0); // 2024-01-07 is a Sunday
    const outHour = local.getUTCHours();
    const outMin = local.getUTCMinutes();
    const dayDelta = local.getUTCDay() - REF_DOW; // -1, 0, or +1
    return { hour: outHour, minute: outMin, days: shiftDays(days, dayDelta) };
  }
  // toLocal: inputs are UTC → construct a UTC Date, read local fields.
  local = new Date(Date.UTC(2024, 0, 7, hour, minute, 0, 0));
  const outHour = local.getHours();
  const outMin = local.getMinutes();
  const dayDelta = local.getDay() - REF_DOW;
  return { hour: outHour, minute: outMin, days: shiftDays(days, dayDelta) };
}

/** Shift each day in the set by delta (mod 7). Null stays null (daily/hourly). */
function shiftDays(days: number[] | null, delta: number): number[] | null {
  if (!days || delta === 0) return days;
  return [...new Set(days.map((d) => ((d + delta) % 7 + 7) % 7))].sort((a, b) => a - b);
}

/** Build a 5-field UTC cron expression from a preset + LOCAL time/day.
 *  The user picks local wall-clock; we convert to UTC for storage. */
export function buildCron(
  preset: RecurrencePreset,
  opts: { hour: number; minute: number; weekday?: number },
): string {
  const { hour, minute, weekday = 1 } = opts;
  if (preset === "hourly") {
    // Hourly: only the minute matters, and minute is zone-invariant for whole-
    // hour offsets — but some zones are :30/:45 off, so convert via a dummy hour.
    const { minute: um } = shiftCronTime("toUtc", 0, minute, null);
    return `${um} * * * *`;
  }
  if (preset === "daily") {
    const u = shiftCronTime("toUtc", hour, minute, null);
    return `${u.minute} ${u.hour} * * *`;
  }
  // weekly
  const u = shiftCronTime("toUtc", hour, minute, [weekday]);
  const day = (u.days && u.days[0] !== undefined) ? u.days[0] : weekday;
  return `${u.minute} ${u.hour} * * ${day}`;
}

/**
 * Best-effort humanizer for a 5-field cron expression, covering the shapes the
 * form produces plus common simple ones. Falls back to the raw expression when
 * it can't confidently describe it (never throws).
 */
export function humanizeCron(expr: string | null | undefined): string {
  if (!expr) return "";
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 5) return expr;
  const [min, hour, dom, mon, dow] = parts;

  // Every N minutes / hourly. (Minute is essentially zone-invariant except in
  // :30/:45-offset zones; we render it as stored — close enough for "hourly".)
  if (hour === "*" && dom === "*" && mon === "*" && dow === "*") {
    if (min === "*") return "Every minute";
    if (/^\*\/\d+$/.test(min)) return `Every ${min.slice(2)} minutes`;
    return `Hourly at :${String(Number(min)).padStart(2, "0")}`;
  }

  const hn = Number(hour);
  const mn = Number(min);
  const timeParsable = !Number.isNaN(hn) && !Number.isNaN(mn);

  // Daily at HH:MM — convert the stored UTC time to the viewer's local time.
  if (dom === "*" && mon === "*" && dow === "*") {
    if (!timeParsable) return expr;
    const { hour: lh, minute: lm } = shiftCronTime("toLocal", hn, mn, null);
    return `Daily at ${fmtTime(lh, lm)}`;
  }

  // Weekly on specific weekday(s): single (3), range (1-5), list (1,3,5),
  // named (MON), or 7-as-Sunday. Convert UTC→local for both time AND the day
  // set (a UTC time near midnight can land on a different local day).
  if (dom === "*" && mon === "*" && dow !== "*") {
    const days = parseDow(dow);
    if (days) {
      if (!timeParsable) return expr;
      const { hour: lh, minute: lm, days: ld } = shiftCronTime("toLocal", hn, mn, days);
      const label = describeDays(ld ?? days);
      return `${label} at ${fmtTime(lh, lm)}`;
    }
  }

  return expr;
}

const _NAMED_DOW: Record<string, number> = {
  SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6,
};

/** Parse a cron day-of-week field into a sorted, de-duped 0–6 list (Sun=0).
 *  Handles single (`3`), range (`1-5`), list (`1,3,5`), named (`MON`), and
 *  7-as-Sunday. Returns null if anything is unrecognised. */
function parseDow(field: string): number[] | null {
  const out = new Set<number>();
  const norm = (tok: string): number | null => {
    const up = tok.toUpperCase();
    if (up in _NAMED_DOW) return _NAMED_DOW[up];
    if (!/^\d$/.test(tok)) return null;
    const n = Number(tok);
    if (n === 7) return 0; // 7 is also Sunday
    return n >= 0 && n <= 6 ? n : null;
  };
  for (const part of field.split(",")) {
    const range = part.split("-");
    if (range.length === 2) {
      const a = norm(range[0]);
      const b = norm(range[1]);
      if (a === null || b === null) return null;
      // Walk the range (handles 1-5; doesn't wrap, which is fine for our cases).
      for (let d = a; d <= b; d++) out.add(d);
    } else {
      const d = norm(part);
      if (d === null) return null;
      out.add(d);
    }
  }
  return out.size ? [...out].sort((x, y) => x - y) : null;
}

/** Turn a day-set into a readable label, with friendly names for the common
 *  weekday/weekend sets. */
function describeDays(days: number[]): string {
  const key = days.join(",");
  if (key === "1,2,3,4,5") return "Weekdays";
  if (key === "0,6") return "Weekends";
  if (days.length === 1) return `${DAY_NAMES[days[0]]}s`;
  // e.g. "Mon, Wed & Fri"
  const short = days.map((d) => DAY_NAMES[d].slice(0, 3));
  if (short.length === 2) return `${short[0]} & ${short[1]}`;
  return `${short.slice(0, -1).join(", ")} & ${short[short.length - 1]}`;
}
